import { Combobox } from '@base-ui/react/combobox'
import { useCallback, useImperativeHandle, useRef } from 'react'

import { Icon } from '../Icon'
import { ComboboxDropdown } from './ComboboxDropdown'
import type { ComboboxModeProps, MultipleProps, SharedProps } from './types'
import { useMultiComboboxState } from './useMultiComboboxState'
import { useTabSelectHighlighted } from './useTabSelectHighlighted'

export type MultiComboboxBaseProps<T> = SharedProps<T> &
  ComboboxModeProps<T> &
  Omit<MultipleProps<T>, 'multiple'>

export function MultiComboboxBase<T>(props: MultiComboboxBaseProps<T>) {
  const {
    ref,
    name,
    getOptionLabel,
    filter,
    isLoading,
    required,
    readOnly,
    disabled,
    placeholder,
    autoFocus,
    classNames,
    portalToRoot = false,
    renderOption,
    emptyMessage,
  } = props

  const {
    items,
    inputValue,
    multiValue,
    selectedKeys,
    showCreate,
    onKeyDown,
    getOptionValue,
    handleInputChange,
    handleValueChange,
    handleClear,
    handleCreate,
  } = useMultiComboboxState(props)

  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    clear: handleClear,
    focus: () => inputRef.current?.focus(),
    blur: () => inputRef.current?.blur(),
  }))

  const isItemEqual = useCallback(
    (a: T, b: T) => getOptionValue(a) === getOptionValue(b),
    [getOptionValue],
  )

  const shouldHideOption = useCallback(
    (option: T) => selectedKeys.has(getOptionValue(option)),
    [selectedKeys, getOptionValue],
  )

  const selectHighlightedOption = useCallback(
    (option: T) => {
      if (selectedKeys.has(getOptionValue(option))) return
      handleValueChange([...multiValue, option])
    },
    [selectedKeys, getOptionValue, handleValueChange, multiValue],
  )

  const { handleItemHighlighted, handleTabKeyDown } = useTabSelectHighlighted(
    selectHighlightedOption,
  )

  const visibleOptionCount = items.filter(o => !shouldHideOption(o)).length
  const popupHasContent =
    visibleOptionCount > 0 || !!isLoading || !!emptyMessage || showCreate

  const captureEnter = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && visibleOptionCount === 0) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    [visibleOptionCount],
  )

  return (
    <Combobox.Root
      multiple
      name={name}
      items={items}
      onInputValueChange={handleInputChange}
      itemToStringLabel={getOptionLabel}
      filter={filter}
      readOnly={readOnly}
      disabled={disabled}
      required={required}
      onItemHighlighted={handleItemHighlighted}
      inputValue={inputValue}
      value={multiValue}
      onValueChange={handleValueChange}
      isItemEqualToValue={isItemEqual}
      {...(props.open !== undefined && { open: props.open })}
      {...(props.onOpenChange && { onOpenChange: props.onOpenChange })}
    >
      <Combobox.Chips
        className={classNames?.root}
        ref={anchorRef}
        onKeyDownCapture={captureEnter}
      >
        <Combobox.Value>
          {(value: T[]) => (
            <>
              {value.map(v => {
                const label = getOptionLabel(v)
                return (
                  <Combobox.Chip
                    key={getOptionValue(v)}
                    className={classNames?.chip}
                    aria-label={label}
                  >
                    <span className={classNames?.chipLabel} title={label}>
                      {label}
                    </span>
                    <Combobox.ChipRemove className={classNames?.chipRemove}>
                      <Icon icon="times" size="xs" />
                    </Combobox.ChipRemove>
                  </Combobox.Chip>
                )
              })}
              <Combobox.Input
                ref={inputRef}
                autoComplete="off"
                className={classNames?.input}
                placeholder={value.length === 0 ? placeholder : undefined}
                autoFocus={autoFocus}
                onKeyDown={e => {
                  if (e.key === 'Tab' && !showCreate) {
                    handleTabKeyDown(e)
                  }
                  if ((e.key === 'Enter' || e.key === 'Tab') && showCreate) {
                    e.preventDefault()
                    handleCreate()
                  }
                  onKeyDown?.(e)
                }}
              />
            </>
          )}
        </Combobox.Value>
        {classNames?.trigger && (
          <Combobox.Trigger className={classNames.trigger}>
            <Icon icon="chevron-down" size="xs" />
          </Combobox.Trigger>
        )}
      </Combobox.Chips>
      <ComboboxDropdown<T>
        classNames={classNames}
        emptyMessage={emptyMessage}
        getOptionLabel={getOptionLabel}
        getOptionValue={getOptionValue}
        isLoading={isLoading}
        popupHasContent={popupHasContent}
        portalContainer={
          portalToRoot ? document.getElementById('root') : undefined
        }
        renderOption={renderOption}
        shouldHideOption={shouldHideOption}
        createLabel={showCreate ? inputValue : undefined}
        onCreate={showCreate ? handleCreate : undefined}
        positionerAnchor={anchorRef}
      />
    </Combobox.Root>
  )
}
